// src/templateManager/TemplateEditor.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Container,
  Stack,
  Title,
  Text,
  Button,
  Group,
  Tabs,
  Badge,
  Alert,
  Loader,
  Paper
} from '@mantine/core';
import {
  IconDeviceFloppy,
  IconFileDescription,
  IconSettings,
  IconShieldCheck,
  IconBug,
  IconAlertCircle,
  IconArrowLeft,
  IconCheck,
  IconEye,
  IconChartBar,
  IconDatabase
} from '@tabler/icons-react';
import { templateSchema } from './schemas/templateSchema';
import { useAutoSave } from './hooks/useAutoSave';
import OverviewTab from './components/tabs/OverviewTab';
import ContextFieldsTab from './components/tabs/ContextFieldsTab';
import RiskAreasTab from './components/tabs/RiskAreasTab';
import RootCausesTab from './components/tabs/RootCausesTab';
import ImpactCategoriesTab from './components/tabs/ImpactCategoriesTab';
import SecurityTab from './components/tabs/SecurityTab';
import RAGConfigTab from './components/tabs/RAGConfigTab';
import PreviewTab from './components/tabs/PreviewTab';

/**
 * TemplateEditor - Main template editor with tabs
 * Supports creating new templates and editing existing ones
 */
function TemplateEditor() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const isNewTemplate = templateId === 'new';

  const [activeTab, setActiveTab] = useState('overview');
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(!isNewTemplate);
  const [error, setError] = useState(null);

  // React Hook Form setup
  const methods = useForm({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: '',
      description: '',
      modelType: 'generative',
      isDefault: false,
      taxonomy: {
        contextFields: {
          useCase: [],
          modelContext: [],
          businessContext: []
        },
        riskAreas: {},
        rootCauses: {},
        causeEvaluationCriteria: {},
        impactCategories: [],
        securityTaxonomy: {},
        securityImpactCategories: {},
        vulnerabilityAssessment: {}
      },
      evaluationCriteriaRefs: {
        riskEvaluationConfigId: 'default_risk_eval',
        controlEvaluationConfigId: 'default_control_eval'
      },
      ragConfig: {
        useScenarioRag: false,
        useControlRag: false,
        riskCatalogues: [],
        policyTypes: ['governance', 'security', 'ai-control'],
        policyDocuments: []
      }
    },
    mode: 'onChange'
  });

  const { handleSubmit, reset, formState: { isDirty, errors }, watch } = methods;
  const formData = watch();

  const fetchTemplate = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch('/.netlify/functions/templates-getTemplate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: templateId })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to fetch template');
      }

      setTemplate(result);

      // Reset form with fetched data
      reset({
        name: result.name,
        description: result.description || '',
        modelType: result.model_type,
        isDefault: result.is_default,
        taxonomy: result.taxonomy,
        evaluationCriteriaRefs: result.evaluation_criteria_refs,
        ragConfig: {
          useScenarioRag: result.use_scenario_rag || false,
          useControlRag: result.use_control_rag || false,
          riskCatalogues: result.risk_catalogues || [],
          policyTypes: result.policy_types || ['governance', 'security', 'ai-control'],
          policyDocuments: result.policy_documents || []
        }
      });

    } catch (err) {
      console.error('[TemplateEditor] Error fetching template:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [templateId, reset]);

  // Fetch template if editing
  useEffect(() => {
    if (!isNewTemplate && templateId) {
      fetchTemplate();
    }
  }, [templateId, isNewTemplate, fetchTemplate]);

  // Save function (for manual and auto-save)
  const saveTemplate = async (data) => {
    try {
      const payload = {
        ...(isNewTemplate ? {} : { id: templateId }),
        name: data.name,
        description: data.description,
        modelType: data.modelType,
        taxonomy: data.taxonomy,
        evaluationCriteriaRefs: data.evaluationCriteriaRefs,
        isDefault: data.isDefault,
        ragConfig: data.ragConfig,
        changeDescription: 'Template updated via editor',
        createdBy: 'user' // TODO: Get from auth context
      };

      const response = await authFetch('/.netlify/functions/templates-saveTemplate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to save template');
      }

      // If creating new template, redirect to edit mode
      if (isNewTemplate && result.template?.id) {
        navigate(`/template-editor/${result.template.id}`, { replace: true });
      }

      return result;

    } catch (err) {
      console.error('[TemplateEditor] Save error:', err);
      throw err;
    }
  };

  // Auto-save
  const { isSaving, lastSaved } = useAutoSave(
    saveTemplate,
    formData,
    {
      interval: 30000, // 30 seconds
      enabled: !isNewTemplate && isDirty, // Only auto-save for existing templates
      shouldSave: (data) => data.name?.length > 0 // Only save if name is set
    }
  );

  // Manual save
  const onSave = handleSubmit(async (data) => {
    await saveTemplate(data);
  });

  // Publish template
  const onPublish = async () => {
    if (!template?.id) return;

    try {
      const response = await authFetch('/.netlify/functions/templates-publishTemplate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: template.id })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to publish template');
      }

      // Refresh template
      await fetchTemplate();

    } catch (err) {
      console.error('[TemplateEditor] Publish error:', err);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Group justify="center">
          <Loader />
        </Group>
      </Container>
    );
  }

  if (error && !template) {
    return (
      <Container size="xl" py="xl">
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <FormProvider {...methods}>
      <Container size="xl" py="md">
        <Stack gap="md">
          {/* Header */}
          <Paper p="md" withBorder>
            <Group justify="space-between">
              <Group>
                <Button
                  variant="subtle"
                  leftSection={<IconArrowLeft size={16} />}
                  onClick={() => navigate('/template-manager')}
                >
                  Back
                </Button>
                <div>
                  <Group gap="xs">
                    <Title order={3}>
                      {isNewTemplate ? 'Create New Template' : template?.name}
                    </Title>
                    {template?.is_published ? (
                      <Badge color="green">Published</Badge>
                    ) : (
                      <Badge color="gray">Draft</Badge>
                    )}
                    {template?.is_default && (
                      <Badge color="yellow">Default</Badge>
                    )}
                  </Group>
                  {template && (
                    <Text size="sm" c="dimmed">
                      Version {template.version} • Last updated {new Date(template.updated_at).toLocaleString()}
                    </Text>
                  )}
                </div>
              </Group>

              <Group>
                {/* Auto-save indicator */}
                {isSaving && (
                  <Group gap="xs">
                    <Loader size="xs" />
                    <Text size="sm" c="dimmed">Saving...</Text>
                  </Group>
                )}
                {lastSaved && !isSaving && (
                  <Group gap="xs">
                    <IconCheck size={16} color="green" />
                    <Text size="sm" c="dimmed">
                      Saved {new Date(lastSaved).toLocaleTimeString()}
                    </Text>
                  </Group>
                )}

                {/* Save button */}
                <Button
                  leftSection={<IconDeviceFloppy size={16} />}
                  onClick={onSave}
                  disabled={!isDirty || isSaving}
                >
                  Save Draft
                </Button>

                {/* Publish button */}
                {!isNewTemplate && (
                  <Button
                    variant="filled"
                    color="green"
                    onClick={onPublish}
                    disabled={isDirty || template?.is_published}
                  >
                    {template?.is_published ? 'Published' : 'Publish Template'}
                  </Button>
                )}
              </Group>
            </Group>

            {/* Validation errors */}
            {Object.keys(errors).length > 0 && (
              <Alert color="red" icon={<IconAlertCircle size={16} />} mt="md">
                Please fix validation errors before saving
              </Alert>
            )}
          </Paper>

          {/* Tabs */}
          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab
                value="overview"
                leftSection={<IconFileDescription size={16} />}
              >
                Overview
              </Tabs.Tab>
              <Tabs.Tab
                value="context"
                leftSection={<IconSettings size={16} />}
              >
                Context
              </Tabs.Tab>
              <Tabs.Tab
                value="riskAreas"
                leftSection={<IconBug size={16} />}
              >
                Risk Areas
              </Tabs.Tab>
              <Tabs.Tab
                value="rootCauses"
                leftSection={<IconBug size={16} />}
              >
                Root Causes
              </Tabs.Tab>
              <Tabs.Tab
                value="impactCategories"
                leftSection={<IconChartBar size={16} />}
              >
                Impact Categories
              </Tabs.Tab>
              <Tabs.Tab
                value="security"
                leftSection={<IconShieldCheck size={16} />}
              >
                Security
              </Tabs.Tab>
              <Tabs.Tab
                value="ragConfig"
                leftSection={<IconDatabase size={16} />}
              >
                RAG Configuration
              </Tabs.Tab>
              <Tabs.Tab
                value="preview"
                leftSection={<IconEye size={16} />}
              >
                Preview
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="overview" pt="lg">
              <OverviewTab />
            </Tabs.Panel>

            <Tabs.Panel value="context" pt="lg">
              <ContextFieldsTab />
            </Tabs.Panel>

            <Tabs.Panel value="riskAreas" pt="lg">
              <RiskAreasTab />
            </Tabs.Panel>

            <Tabs.Panel value="rootCauses" pt="lg">
              <RootCausesTab />
            </Tabs.Panel>

            <Tabs.Panel value="impactCategories" pt="lg">
              <ImpactCategoriesTab />
            </Tabs.Panel>

            <Tabs.Panel value="security" pt="lg">
              <SecurityTab />
            </Tabs.Panel>

            <Tabs.Panel value="ragConfig" pt="lg">
              <RAGConfigTab />
            </Tabs.Panel>

            <Tabs.Panel value="preview" pt="lg">
              <PreviewTab />
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Container>
    </FormProvider>
  );
}

export default TemplateEditor;
